import React from 'react';
import { UnitText } from '../../interfaces/panel-data';
import { formatCompactUnitCount, formatExactCount } from '../../utils/format-count';
import { pluraliseUnit } from '../../utils/format-unit';
import './CountValue.scss';

interface CountValueProps {
  count: number;
  /** The unit vocabulary for this count. Omit for a count that carries no unit. */
  text?: UnitText;
  /** Rendered bold, for the one count in a sentence that is its subject. */
  emphasis?: boolean;
}

/**
 * A count shown compactly, with the exact number in its tooltip.
 *
 * The two always travel together. `formatCompactCount` is lossy by design — `1.2K` is any of a hundred
 * values — so a compact count on its own asks the reader to accept a number they cannot check. Reaching
 * for the compact formatter directly is the mistake this component exists to prevent; render this
 * instead, and the exact figure is one hover away wherever the compact one appears.
 *
 * `title` is deliberately the whole phrase including the unit rather than the digits alone, because a
 * tooltip reading "1,247" against a bar labelled "1.2K pages" leaves the reader to assume the unit
 * carried over. The `aria-label` matches it, so a screen reader is given the exact count rather than
 * the abbreviation — the compact form is a space compromise for a visual column, and there is no
 * reason to pass that compromise on to a reader who is not looking at the column.
 */
export const CountValue: React.FC<CountValueProps> = ({ count, text, emphasis = false }) => {
  const unitText = text ?? {};
  const unit = pluraliseUnit(unitText, count);
  const exact = formatExactCount(count);
  const exactPhrase = unit ? `${exact} ${unit}` : exact;

  return (
    <span
      className={`ovp-count ${emphasis ? 'ovp-count--emphasis' : ''}`}
      title={exactPhrase}
      aria-label={exactPhrase}
    >
      {formatCompactUnitCount(count, unitText)}
    </span>
  );
};
